<template>
  <div class="content t-c">
    <title-bar :title="$lang.avatar_title"></title-bar>
    <div class="content-c">
      <div class="content-c--c" :key="timerKey">
        <div
          class="content-c--img"
          v-for="(item, index) in imgList"
          :key="index"
          @click="getAvatar(index)"
        >
          <!-- 装饰背景 -->
          <div
            v-if="index === selectedHeadUrl"
            class="avatar-bg"
          ></div>

          <!-- 头像图片 -->
          <div
            v-if="index === selectedHeadUrl"
            class="avatar-img"
            :style="{ background: 'url(' + item + ') no-repeat center/cover' }"
          ></div>
          <div
            v-if="index !== selectedHeadUrl"
            class="avatar-img2"
            :style="{ background: 'url(' + item + ') no-repeat center/cover' }"
          ></div>
        </div>
      </div>
      <van-button @click="Submit" class="custom-button" :loading="submitting">
        {{ $lang.Enviar }}
      </van-button>
    </div>
  </div>
</template>
<script>
import { SetHeadUrl } from '@/api/common'
import { failMessage, handleNeedLogin, isSafeWriteSuccess } from './safeFlow'
import { safeGetStorageItem, safeLocalStorage } from '@/utils/safeStorage'

function getLocalStorage() {
  try {
    if (typeof safeLocalStorage === 'function') return safeLocalStorage()
  } catch (e) { return null }
  return null
}

function avatarFailureMessage(error, fallback) {
  const message = failMessage(error, fallback)
  return isRawAvatarTechnicalMessage(message) ? fallback : message
}

function isRawAvatarTechnicalMessage(message) {
  const text = String(message || '').trim()
  return !text ||
    /https?:\/\//i.test(text) ||
    /\n\s*at\s+|\bat\s+\S+\s*\(/i.test(text) ||
    /\b(typeerror|referenceerror|syntaxerror|rangeerror|urierror|axioserror)\b/i.test(text) ||
    /\b(econn[a-z_]*|etimedout|enotfound|stack trace|traceid|trace id|network error|request failed|failed to fetch|status code|timeout)\b/i.test(text)
}

const MINE_BLOCKED_MARKER_KEYS = [
  'failClosed',
  'fail_closed',
  'falseProof',
  'false_proof',
  'readOnly',
  'read_only',
  'readOnlyAction',
  'read_only_action',
  'readOnlyMode',
  'read_only_mode',
  'readBlocked',
  'read_blocked',
  'readUnavailable',
  'read_unavailable',
  'writeBlocked',
  'write_blocked',
  'backendBlocked',
  'backend_blocked',
  'backendFailure',
  'backend_failure',
  'technicalError',
  'technical_error',
  'technicalException',
  'technical_exception',
  'technicalMarker',
  'technical_marker',
  'mock',
  'mockMode',
  'mock_mode',
  'mockFallback',
  'mock_fallback',
  'fallback',
  'fallbackUsed',
  'fallback_used',
  'stub',
  'placeholder',
  'placeholderMode',
  'placeholder_mode',
  'placeholderResponse',
  'placeholder_response',
  'unimplemented',
  'notImplemented',
  'not_implemented',
  'notSupported',
  'not_supported',
  'notConnected',
  'not_connected',
  'unsupported',
  'unavailable',
  'dataUnavailable',
  'data_unavailable',
  'routeUnavailable',
  'route_unavailable',
  'queryOnly',
  'query_only',
  'onlyQuery',
  'only_query',
  'blocked'
]
const MINE_FALSE_PROOF_KEYS = ['writeProof', 'write_proof', 'affectedRowsProof', 'affected_rows_proof']
const MINE_STRICT_WRITE_PROOF_KEYS = ['writeProof', 'write_proof', 'serverProof', 'server_proof', 'affectedProof', 'affected_proof', 'affectedRowsProof', 'affected_rows_proof']
const MINE_WRITE_COUNT_PROOF_KEYS = ['affectedRows', 'affected_rows', 'affected', 'rowCount', 'row_count']
const MINE_FALSEY_PROOF_TOKENS = ['0', 'false', 'null', 'undefined', 'none', 'n/a', 'na', 'mock', 'placeholder', 'fallback', 'stub', 'blocked']
const MINE_BLOCKED_PROOF_TOKENS = ['backendfailure', 'backend_failure', 'technicalmarker', 'technical_marker']
const MINE_BLOCKED_VALUE_TOKENS = [
  'stub',
  'mock',
  'fallback',
  'placeholder',
  'read_only',
  'read-only',
  'read only',
  'readonly',
  'query_only',
  'query-only',
  'query only',
  'only_query',
  'only-query',
  'only query',
  'unavailable',
  'unsupported',
  'not_connected',
  'not-connected',
  'not connected',
  'failclosed',
  'fail_closed',
  'fail-closed',
  'writeblocked',
  'write_blocked',
  'write-blocked',
  'backendblocked',
  'backend_blocked',
  'backend-blocked',
  'backendfailure',
  'backend_failure',
  'backend-failure',
  'technicalerror',
  'technical_error',
  'technical-error',
  'technicalexception',
  'technical_exception',
  'technical-exception',
  'technicalmarker',
  'technical_marker',
  'technical-marker',
  'falseproof',
  'false_proof',
  'false-proof',
  'blocked'
]

function mineToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function hasActiveMineBlockedMarkerValue(value) {
  if (value === false || value === 0 || value === null || value === undefined || value === '') return false
  return !['false', '0', 'no', 'off'].includes(mineToken(value))
}

function hasMineBlockedValueToken(value) {
  const token = mineToken(value)
  if (!token || token.includes('unblocked') || token.includes('not blocked')) return false
  return MINE_BLOCKED_VALUE_TOKENS.some(marker => token.includes(marker))
}

function hasMineBlockedWriteMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasMineBlockedWriteMarker(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    const token = String(value == null ? '' : value).trim().toLowerCase()
    if (MINE_BLOCKED_MARKER_KEYS.includes(key) && hasActiveMineBlockedMarkerValue(value)) return true
    if (MINE_FALSE_PROOF_KEYS.includes(key) && (value === false || value === 0 || ['false', '0', 'no', 'off', 'placeholder'].includes(token))) return true
    if (hasMineBlockedValueToken(value)) return true
    return value && typeof value === 'object' && hasMineBlockedWriteMarker(value, depth + 1, seen)
  })
}

function hasPositiveMineWriteCount(value) {
  if (typeof value === 'number') return Number.isInteger(value) && value > 0
  if (typeof value !== 'string') return false
  const token = mineToken(value)
  return /^\d+$/.test(token) && !/^0+$/.test(token)
}

function hasStrictMineProofValue(value) {
  if (Array.isArray(value) || (value && typeof value === 'object')) return false
  if (value === true || value === false || value === 0) return false
  const token = mineToken(value)
  return !!token &&
    token !== 'true' &&
    !MINE_FALSEY_PROOF_TOKENS.includes(token) &&
    !token.includes('mock') &&
    !token.includes('placeholder') &&
    !token.includes('fallback') &&
    !token.includes('stub') &&
    !token.includes('read_only') &&
    !token.includes('read-only') &&
    !token.includes('read only') &&
    !token.includes('readonly') &&
    !token.includes('blocked') &&
    !MINE_BLOCKED_PROOF_TOKENS.some(marker => token.includes(marker)) &&
    !token.includes('unavailable') &&
    !token.includes('unimplemented') &&
    !token.includes('fail') &&
    !token.includes('error')
}

function hasStrictMineWriteProof(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasStrictMineWriteProof(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    if (MINE_WRITE_COUNT_PROOF_KEYS.includes(key) && hasPositiveMineWriteCount(value)) return true
    if (MINE_STRICT_WRITE_PROOF_KEYS.includes(key) && hasStrictMineProofValue(value)) return true
    return value && typeof value === 'object' && hasStrictMineWriteProof(value, depth + 1, seen)
  })
}

function isMineSafeWriteSuccess(res) {
  return isSafeWriteSuccess(res) && hasStrictMineWriteProof(res) && !hasMineBlockedWriteMarker(res)
}

export default {
  name: 'Avatar',
  components: {},
  data() {
    return {
      imgList: [
        require('@/assets/img/avatar/avatar_0.jpg'),
        require('@/assets/img/avatar/avatar_1.jpg'),
        require('@/assets/img/avatar/avatar_2.jpg'),
        require('@/assets/img/avatar/avatar_3.jpg'),
        require('@/assets/img/avatar/avatar_4.jpg'),
        require('@/assets/img/avatar/avatar_5.jpg'),
        require('@/assets/img/avatar/avatar_6.jpg'),
        require('@/assets/img/avatar/avatar_7.jpg')
      ],
      timerKey: 0,
      selectedHeadUrl: null,
      submitting: false
    }
  },
  mounted() {
    if (!this.ensureLogin()) return
    const headUrl = parseInt(this.$route.query.headUrl, 10)
    if (Number.isInteger(headUrl) && headUrl >= 0 && headUrl < this.imgList.length) {
      this.selectedHeadUrl = headUrl
    }
  },
  methods: {
    ensureLogin() {
      if (safeGetStorageItem(getLocalStorage(), 'token')) return true
      if (this.$bus) this.$bus.$emit('openLogin')
      this.$jumpTo('/home', {}, { replace: true })
      return false
    },
    getAvatar(i) {
      this.selectedHeadUrl = i
      this.timerKey++
    },
    async Submit() {
      if (this.submitting) return
      if (!this.ensureLogin()) return
      const headUrl = this.selectedHeadUrl
      if (!Number.isInteger(headUrl) || headUrl < 0 || headUrl >= this.imgList.length) {
        this.$toast({ message: this.$lang.noempt || 'No data', icon: 'cross' })
        return
      }
      this.submitting = true
      try {
        const data = await SetHeadUrl({
          headUrl
        })
        if (handleNeedLogin(this, data)) return
        if (isSafeWriteSuccess(data) && isMineSafeWriteSuccess(data)) {
          this.$router.go(-1)
          this.$toast({
            message: this.$lang.Sucesso || 'Success',
            icon: 'success'
          })
        } else {
          this.$toast({ message: failMessage(data, 'Save failed'), icon: 'cross' })
        }
      } catch (e) {
        this.$toast({ message: avatarFailureMessage(e, this.$lang.feature_unavailable || 'This feature is temporarily unavailable. Please try again later.'), icon: 'cross' })
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
<style lang="less" scoped>
.content-c--c {
  display: flex;
  flex-wrap: wrap; // ✅ 允许换行
  justify-content: space-between; // 头像之间平均分布
  margin: 10px;
  background: @cont-bg;
  border-radius: 15px;
}
.active {
  background: url(../../../assets/img/avatar/img_fg.png) no-repeat;
  background-size: 100% 100%;
  height: 80px;
  width: 80px;
  top: -8px;
  left: -8px;
  position: absolute;
  //   border-radius: 80px;
  // border: 2px solid #076237;
}
.custom-button {
  width: 92%;
  margin-top: 20px;
}

.content-c--img {
  position: relative;
  width: 25%;
  height: 102px;
  display: flex; // 启用 flex 布局
  align-items: center; // 垂直居中
  justify-content: center; // 水平居中
}

.avatar-bg {
  position: absolute;
  top: 0;
  width: 102px;
  height: 102px;
  background: url(../../../assets/img/avatar/img_fg.png) no-repeat center;
  background-size: 100% 100%;
  z-index: 0; // ✅ 放在下层
}

.avatar-img {
  position: relative;
  width: 67px;
  height: 67px;
  margin: auto;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  z-index: 1; // ✅ 放在上层
  border: 2px solid #85cdf9; // ✅ 添加绿色边框
  border-radius: 50%; // ✅ 设置为圆形
  box-sizing: border-box; // ✅ 确保边框不撑大尺寸
}
.avatar-img2 {
  position: relative;
  width: 65px;
  height: 65px;
  margin: auto;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  z-index: 1; // ✅ 放在上层
}
</style>
